import { Card, Checkbox, Collapse, Flex, Space } from 'antd'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { FilterParamNames } from '../../../../../../constants/assignment-filter-params'
import {
    FLAGGED_FOR_MYSELF_PARAM,
    FLAGGED_PARAM,
    MISMATCH_PARAM,
    TEST_SET_ONLY_PARAM,
} from '../../../../../../constants/query-params'
import useQueryParams from '../../../../../../hooks/useQueryParams'
import useQueryParamSetter from '../../../../../../hooks/useQueryParamSetter'
import useQueryParam from '../../../../../../hooks/useQueryParam'
import ConfidenceFilters from './ConfidenceFilters'
import ReviewFilters from './ReviewStatusFilters'
import ResetFiltersButton from './ResetFiltersButton'
import assignmentSelectors from '../../../../../../store/selectors/assignment.selectors'
import { useSelector } from 'react-redux'
import AssigneeFilters from './AssigneeFilters'
import { useFetchAssignmentSharedWithQuery } from '../../../../../../store/apis/shared-assignment.api'
import { fetchAssignmentSharedWithSelectors } from '../../../../../../store/apis/utils/selectors/shared-assignment.api.selectors'
import { isMba } from '../../../../../../utils/assignment.helper'
import useResetFilters from './useResetFilters'
import useRubricContext from '../../../../../../hooks/useRubricContext'
import { useThemeColors } from '../../../../../../hooks/useThemeColors'

const FiltersCard = () => {
    const colors = useThemeColors()
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const isSupervisor = useSelector(assignmentSelectors.isSupervisor)
    const assignmentType = useSelector(assignmentSelectors.type)

    const { token, layerId: questionRubricId } = useParams()
    const setQueryParam = useQueryParamSetter()
    const params = useQueryParams(FilterParamNames)
    const mismatch = params[0]
    const escalated = params[1]
    const escalatedForMyself = params[4]

    const { data: users } = useFetchAssignmentSharedWithQuery(
        { token },
        {
            selectFromResult: result => fetchAssignmentSharedWithSelectors.selectOnlyUsers(result),
        },
    )

    const { questionRubric } = useRubricContext()

    const allowFilteringByAssignees = useMemo(() => {
        if (!users?.length) return false

        if (isOwner || isSupervisor) return true
        return false
    }, [isOwner, isSupervisor, users, questionRubricId])

    const [activePanels, setActivePanels] = useState(['review-status', 'confidence', 'assignee', 'properties'])
    const testSetOnlyParam = useQueryParam(TEST_SET_ONLY_PARAM) === 'true'
    useResetFilters(questionRubricId, testSetOnlyParam)

    const handleCollapseChange = newActivePanels => setActivePanels(newActivePanels)

    const handleMismatchChange = event => setQueryParam({ [MISMATCH_PARAM]: event.target.checked ? 'true' : undefined })

    const handleEscalatedChange = event => setQueryParam({ [FLAGGED_PARAM]: event.target.checked ? 'true' : undefined })

    const handleEscalatedForMyselfChange = event =>
        setQueryParam({ [FLAGGED_FOR_MYSELF_PARAM]: event.target.checked ? 'true' : undefined })

    const collapsePanels = [
        {
            key: 'review-status',
            label: 'Review Status',
            children: <ReviewFilters />,
        },
        ...(questionRubricId !== 'all' && (isMba(assignmentType) || testSetOnlyParam)
            ? [
                  {
                      key: 'confidence',
                      label: 'Confidence',
                      children: <ConfidenceFilters />,
                  },
              ]
            : []),
        ...(allowFilteringByAssignees
            ? [
                  {
                      key: 'assignee',
                      label: 'Assignee',
                      children: <AssigneeFilters />,
                  },
              ]
            : []),
        {
            key: 'properties',
            label: 'Properties',
            children: (
                <Space direction='vertical'>
                    {questionRubricId !== 'all' && !isMba(assignmentType) && (testSetOnlyParam || !questionRubric?.lastTrained) && (
                        <Checkbox checked={mismatch} onChange={handleMismatchChange}>
                            Mismatch
                        </Checkbox>
                    )}
                    <Checkbox checked={escalated} onChange={handleEscalatedChange}>
                        Escalated
                    </Checkbox>
                    <Checkbox checked={escalatedForMyself} onChange={handleEscalatedForMyselfChange}>
                        Escalated for Myself
                    </Checkbox>
                </Space>
            ),
        },
    ]

    return (
        <Card
            title={
                <Flex style={{ width: '100%', padding: '8px 0' }} justify='space-between' align='center'>
                    <div>Filters</div>
                    <ResetFiltersButton />
                </Flex>
            }
            size='small'
            style={{ backgroundColor: colors.background, borderColor: colors.borderColor }}
        >
            <Collapse
                ghost
                bordered={false}
                onChange={handleCollapseChange}
                activeKey={activePanels}
                items={collapsePanels}
            />
        </Card>
    )
}

export default FiltersCard
